import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import {
  ConditionChoice,
  ManifestEntry,
  buildManifestCsv,
  buildNSPartEntry,
  buildSerialisedEntry,
  sortStockByCondition,
} from './PartsShipmentsHelpers';
import { usePartOptionsSearch } from './usePartOptionsSearch';
import { LocationNodeData } from '../../../../../@types/live-search.type';
import { NSPartLocationList, PartObject } from '../../../../../@types/part.type';
import {
  PartTypeActionLiveSearchItem,
  PartTypeActionLiveSearchItemCategoryCode,
} from '../../../../../@types/partType.type';
import { getPartById } from '../../../../../api/part';
import { getNSPartByPartTypeAndLocation } from '../../../../../api/parts';
import { NSPartLocationListKey, PartKey } from '../../../../../configs/part';

export interface UseManifestArgs {
  serviceId: number;
  clientContractIds?: number[];
  source: LocationNodeData | null;
}

const applyPartEnrichment = (
  prev: ManifestEntry[],
  partId: number,
  part: PartObject
): ManifestEntry[] =>
  prev.map((m) =>
    m.partId === partId
      ? {
          ...m,
          fleetTags: part.fleetTagName,
          fleetTagId: part.fleetTagId,
          softwareVersionId: part.softwareVersionId,
          hardwareRevisionId: part.hardwareRevisionId,
          firmwareVersionId: part.firmwareVersionId,
        }
      : m
  );

export const useManifest = ({ serviceId, clientContractIds, source }: UseManifestArgs) => {
  const [manifest, setManifest] = useState<ManifestEntry[]>([]);
  const [conditionChoice, setConditionChoice] = useState<ConditionChoice | null>(null);
  const [notice, setNotice] = useState('');
  const [stockResolvePending, setStockResolvePending] = useState(false);
  const search = usePartOptionsSearch({ clientContractIds, source });
  const queryClient = useQueryClient();

  // Latest values for async callbacks (stock resolution resolves after state
  // may have moved on).
  const manifestRef = useRef(manifest);
  const sourceRef = useRef(source);
  useEffect(() => {
    manifestRef.current = manifest;
    sourceRef.current = source;
  });

  // Manifest entries are resolved against a specific source location (serial
  // search scope, NSPart stock records), so a source change invalidates them.
  const prevSourceIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const newId = source?.id;
    if (newId == null) return; // ignore transient null while the field clears
    const prevId = prevSourceIdRef.current;
    prevSourceIdRef.current = newId;
    if (prevId == null || prevId === newId) return;
    setConditionChoice(null);
    if (manifestRef.current.length > 0) {
      setManifest([]);
      setNotice('Manifest cleared — items must come from the selected source location.');
    }
  }, [source?.id]);

  const addSerialisedEntry = (item: PartTypeActionLiveSearchItem) => {
    const partId = item.partId as number;
    if (manifestRef.current.some((m) => m.partId === partId)) {
      setNotice(`Serial ${item.serial1} is already in the manifest.`);
      return;
    }
    setManifest((prev) => [...prev, buildSerialisedEntry(item)]);
    // The live-search result lacks the fleet tag and version IDs that the BFF
    // snapshots onto serialised manifest rows, so backfill them from the part
    // record. Best-effort: the entry stays valid if this fails.
    queryClient
      .fetchQuery({
        queryKey: [PartKey, partId],
        queryFn: ({ signal }) => getPartById(partId, signal),
        staleTime: 60 * 1000,
      })
      .then((part) => setManifest((prev) => applyPartEnrichment(prev, partId, part)))
      .catch(() => undefined);
  };

  const addNSPartEntry = (
    meta: Pick<ConditionChoice, 'partTypeId' | 'partTypeName' | 'partTypeNo'>,
    row: NSPartLocationList
  ) => {
    setManifest((prev) => [...prev, buildNSPartEntry(meta, row)]);
    setConditionChoice(null);
  };

  const resolveConsumableStock = async (item: PartTypeActionLiveSearchItem) => {
    const requestSourceId = source?.id;
    if (!requestSourceId) return;
    const meta = {
      partTypeId: item.partTypeId,
      partTypeName: item.partTypeName || item.name || '-',
      partTypeNo: item.partTypeNumber || '-',
    };
    setStockResolvePending(true);
    try {
      // Same cache identity as useGetNSPartByPartTypeAndLocation so the
      // standalone dispatch page and this form share lookups.
      const rows = await queryClient.fetchQuery({
        queryKey: [NSPartLocationListKey, meta.partTypeId, requestSourceId, false, true],
        queryFn: ({ signal }) =>
          getNSPartByPartTypeAndLocation(meta.partTypeId, requestSourceId, false, true, signal),
      });
      // The user changed source while the lookup was in flight — the rows no
      // longer describe stock at the selected source.
      if (sourceRef.current?.id !== requestSourceId) return;
      const available = (rows ?? []).filter(
        (row) => (row.quantity ?? 0) > 0 && !manifestRef.current.some((m) => m.nsPartId === row.id)
      );
      if (available.length === 0) {
        setNotice(`No available stock of ${meta.partTypeName} at the source location.`);
        return;
      }
      const sorted = sortStockByCondition(available);
      if (sorted.length === 1) {
        addNSPartEntry(meta, sorted[0]);
        return;
      }
      setConditionChoice({ ...meta, rows: sorted });
    } catch {
      setNotice(`Could not check stock of ${meta.partTypeName} — try adding it again.`);
    } finally {
      setStockResolvePending(false);
    }
  };

  const addManifestItem = (item: PartTypeActionLiveSearchItem) => {
    setNotice('');
    search.clearSearch();
    if (item.partId) {
      addSerialisedEntry(item);
      return;
    }
    // Only consumable part types resolve to NSPart stock records; serialised
    // (and kit) part types must be added by their specific serial number.
    if (item.categoryCode !== PartTypeActionLiveSearchItemCategoryCode.Consumable) {
      setNotice(
        `${item.partTypeName || item.name || 'This part type'} is serialised — search for the specific serial number to add it.`
      );
      return;
    }
    resolveConsumableStock(item);
  };

  const chooseCondition = (row: NSPartLocationList) => {
    if (conditionChoice) addNSPartEntry(conditionChoice, row);
  };
  const dismissConditionChoice = () => setConditionChoice(null);

  const removeItem = (key: string) => setManifest((p) => p.filter((m) => m.key !== key));
  const updateQty = (key: string, q: number) =>
    setManifest((p) =>
      p.map((m) =>
        m.key === key
          ? { ...m, quantity: Math.min(Math.max(1, q), m.maxQuantity ?? Number.MAX_SAFE_INTEGER) }
          : m
      )
    );

  const exportManifest = () => {
    const blob = new Blob([buildManifestCsv(manifest)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `manifest-JOB-${serviceId}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return {
    manifest,
    serialised: manifest.filter((m) => m.type === 'serialised'),
    nonSerialised: manifest.filter((m) => m.type === 'non-serialised'),
    partSearchInput: search.partSearchInput,
    partOptions: search.partOptions,
    partSearchPending: search.partSearchPending,
    conditionChoice,
    notice,
    stockResolvePending,
    setPartSearchInput: search.setPartSearchInput,
    addManifestItem,
    chooseCondition,
    dismissConditionChoice,
    removeItem,
    updateQty,
    exportManifest,
  };
};
